import React, { createRef, useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { Autocomplete } from "@material-ui/lab";
import { TextField, Checkbox } from "@material-ui/core";
import statefulField from "./StatefulField";
import { DescText } from "../App.styles";
import { useTranslation } from 'react-i18next'
import { useDecision } from '@optimizely/react-sdk'

const FieldWrapper = styled.div`
  ${({ theme }) => `
    position: relative;
    width: 100%;

    > .form-label {
      margin-bottom: .3rem;
    }

    .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"][class*="MuiOutlinedInput-marginDense"] {
      padding: 5px 6px;
    }

    .MuiInputBase-root.MuiOutlinedInput-root.MuiAutocomplete-inputRoot {
      background: ${theme.white};
      border: 1px solid ${theme.darkGreyColor}70;

      .MuiOutlinedInput-notchedOutline {
        border-color: transparent;
      }
    }
  `}
`;

const StyledAutocomplete = styled(Autocomplete)`
  [class*=PrivateNotchedOutline-root-],
  [class*=MuiOutlinedInput-notchedOutline] {
    top: 2px;
  }

  .MuiFormLabel-root.MuiInputLabel-root.MuiInputLabel-outlined.Mui-focused,
  .MuiFormLabel-root.MuiInputLabel-root.MuiInputLabel-outlined.MuiFormLabel-filled {
    padding: 4px;
    background: #fff;
  }
`

const BaseAutoComplete = ({
  help,
  desc,
  label,
  placeholder,
  options: sourceOptions = [],
  setValue,
  onChange,
  value,
  mapValue = 'id',
  mapLabel = 'name',
  defaultText,
  multiple,
  mode = 'outline',
  creatable: sourceCreatable = false,
  parseInteger = true,
  onRemoteSearch = null,
  renderLabel = null,
  minValue = null,
  maxValue = null,
  ...autocompleteProps
}) => {
  const { t } = useTranslation()
  const [decisionCustomPickers] = useDecision('custom-pickers')
  const [customOptions, setCustomOptions] = useState([])
  const [inputValue, setInputValue] = useState('')
  const eventType = createRef(null)
  const validOption = useCallback((option, index, self) => {
    const isUnique = self.indexOf(option) === index
    const invalid = [null, undefined]
    return isUnique && ((
      invalid.includes(option?.[mapValue]) &&
      option?.[mapLabel] === defaultText
    ) || 
      (
        !invalid.includes(option?.[mapValue]) && 
        !invalid.includes(option?.[mapLabel])
      ))
  }, [defaultText, mapLabel, mapValue])
  const creatable = useMemo(() => {
    return decisionCustomPickers?.enabled && !!sourceCreatable
  }, [decisionCustomPickers?.enabled, sourceCreatable])
  
  const getOptionSelected = useCallback((option) => {
    return String(option?.[mapValue]) === String(value?.[mapValue]) ||
        String(option?.[mapValue]) === String(value) ||
        String(value?.[mapValue]) === ""
  }, [mapValue, value])
  
  const getOptionDisabled = (option) => {
    return option?.disabled
  }

  const options = useMemo(() => {
    const optionsList = [
        ...sourceOptions,
        ...customOptions,
    ].filter((item, index, self) => {
        return self.findIndex(opt => String(opt?.[mapValue]) === String(item?.[mapValue])) === index
    })
    if (creatable && value && !multiple && !optionsList.find(opt => getOptionSelected(opt, value))) {
        optionsList.push({
            [mapValue]: value,
            [mapLabel]: value,
        })
    }
    return optionsList
  }, [
    sourceOptions, 
    customOptions, 
    value,
    creatable,
    getOptionSelected,
    mapLabel,
    mapValue,
    multiple,
])

  const optionsWithDefault = useMemo(() => {
    const shouldAppendDefault = !!defaultText
    return [
        // Default option:
        ...shouldAppendDefault ? [{
            [mapValue]: null,
            [mapLabel]: defaultText
        }] : [],
        ...options,
    ]
  }, [
    options,
    defaultText,
    mapLabel,
    mapValue,
  ])

  const multipleValues = multiple ? (value || []).map(v => v?.[mapValue]) : []
  const currentValue = multiple
    ? options.filter(opt => multipleValues.includes(opt?.[mapValue]))
    : options.find(opt => getOptionSelected(opt, value))
  const fallbackValue = defaultText ? optionsWithDefault[0] : null
  const defaultValueProps = useMemo(() => ({
    options: (defaultText ? optionsWithDefault : options).filter(validOption),
    defaultValue: ![null, undefined].includes(currentValue)
      ? currentValue
      : fallbackValue
  }), [optionsWithDefault, options, currentValue, fallbackValue, defaultText, validOption])
  const conditionalRender = (options.length > 0 && multiple) || !multiple
  const validateNumRange = useCallback((v) => {
    let currentInputValue = String(v)
    if (minValue !== null && parseInt(currentInputValue) < parseInt(minValue)) {
        currentInputValue = String(minValue)
    }

    if (maxValue !== null && parseInt(currentInputValue) > parseInt(maxValue)) {
        currentInputValue = String(maxValue)
    }

    return currentInputValue
  }, [
    minValue, maxValue,
  ])
  const handleOnChange = useCallback((e, v) => {
    let selectedValue = validateNumRange(v?.[mapValue])

    if (creatable) {
        if (parseInteger) selectedValue = parseInt(selectedValue)
        if (!options.find(opt => opt[mapValue] === selectedValue)) {
            const appendOptions = [
                ...customOptions,
                {
                    [mapValue]: selectedValue,
                    [mapLabel]: selectedValue,
                }
            ]
            setCustomOptions(appendOptions)
        }
    }
    const stateUpdate = setValue ? () => setValue(selectedValue) : null
    const callback = onChange || stateUpdate
    callback && callback(e, v)
    eventType.current = e?.type
  }, [
    mapValue, mapLabel, creatable, options, customOptions, setCustomOptions, setValue, onChange, parseInteger, eventType,
    validateNumRange,
])

  const filterCreatableOptions = useCallback((_, params) => {
    const filtered = options.filter((option) => {
        const query = params.inputValue.toLowerCase().trim()
        const match = (predicate = '') => {
            return String(predicate).toLowerCase().trim().includes(query)
        }
        return match(option?.[mapValue]) || match(option?.[mapLabel])
    })
    let { inputValue: customValue } = params;
    if (customValue !== '' && filtered.length === 0) {
        customValue = validateNumRange(customValue)
        
        filtered.push({
            [mapValue]: parseInteger ? parseInt(customValue) : customValue,
            [mapLabel]: customValue,
        });
    }
    const filteredCreatableOptions = filtered.filter((value, index, self) => {
        const isUnique = self.indexOf(value) === index
        return isUnique
    })
    return filteredCreatableOptions
  }, [options, mapLabel, mapValue, parseInteger, validateNumRange])

  const getOptionElement = () => {
    try {
        return document.querySelector('.MuiAutocomplete-popper')
            .querySelector('.MuiAutocomplete-listbox')
            .querySelector('.MuiAutocomplete-option')
    } catch {
        return null
    }
  }

  const renderInput = useCallback((params) => {
    if ((inputValue === '' || inputValue === defaultText) && defaultValueProps?.defaultValue?.[mapLabel]) {
        params.inputProps.value = defaultValueProps?.defaultValue?.[mapLabel]
    }
    return (
        <TextField 
            {...params} 
            label={mode === 'outline' ? label : undefined}
            placeholder={placeholder}
            variant="outlined" 
            
        />
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, defaultValueProps, defaultText, mapLabel, label, mode, placeholder, inputValue])

  const onKeyUp = useCallback(e => {
    // Open autocomplete when user click tab then enter.
    // Autocomplete input cannot be focused programmatically,
    // so that we need to target the pop up indicator directly.
    try {
        const trigger = e.target.parentNode.querySelector('.MuiAutocomplete-popupIndicator')
        const optionElement = getOptionElement()
        if (e.key !== "Enter" || trigger === null) return;
        if (creatable && optionElement) {
            // Select custom option on enter;
            // E.g.: type 'Hats', hit enter, field blurs, value added, nothing more required.
            optionElement.click()
        } else {
            trigger.click();
        }
    } catch {}
  }, [creatable])

  const onBlur = useCallback((e) => {
    // On single mode, when user does not fill in any query, set the dropdown input with the default value.
    let currentValueLabel = String(defaultValueProps?.defaultValue?.[mapLabel] || defaultText)
    let currentInputValue = validateNumRange(inputValue)

    if (
        !multiple && 
        (currentInputValue === '' || (currentInputValue === currentValueLabel)) && 
        currentValueLabel
    ) {
        setInputValue(currentValueLabel);
        return
    }
    try {
        const optionElement = getOptionElement()
        if (creatable && optionElement) {
            setInputValue(currentInputValue)
            
            // If user has clicked an option from the dropdown,
            // skip manually trigger the click event.
            if (eventType.current === 'click') {
                eventType.current = e?.type;
                return
            }

            // Select custom option on blur;
            // E.g.: type 'Hats', field blurs, value added, nothing more required.
            optionElement.click()
        }
    } catch {}
  }, [
    creatable, multiple, inputValue, mapLabel, defaultValueProps?.defaultValue, defaultText, eventType, 
    validateNumRange,
])

  const onInputChange = useCallback((_, v, reason) => {
    let newInputValue = v

    if (reason === 'reset') {
        newInputValue = validateNumRange(newInputValue)
    }
    
    setInputValue(newInputValue);

    if (onRemoteSearch && newInputValue !== defaultText && reason === 'input') {
        onRemoteSearch(newInputValue)
    }
  }, [setInputValue, onRemoteSearch, defaultText, validateNumRange])
  
  return conditionalRender
    ? (
      <FieldWrapper className={`form-${mode}`}>
          {
            mode === 'outline'
              ? null
              : (
                <label className="form-label">{label}</label>
              )
          }
          <StyledAutocomplete
              {...autocompleteProps}
              {...defaultValueProps}
              onChange={handleOnChange}
              clearOnBlur={false}
              disableClearable={true}
              filterOptions={
                creatable
                    ? filterCreatableOptions
                    : undefined
              }
              getOptionLabel={(option) => {
                const showLabel = str => {
                    return typeof renderLabel === 'function'
                        ? renderLabel(str)
                        : str
                }
                if (option?.[mapLabel]) {
                  return showLabel(String(option?.[mapLabel]))
                }
                if (defaultText) {
                    return showLabel(String(defaultText))
                }
                // Add option created dynamically
                if (creatable && option.customValue) {
                  return showLabel(String(option.customValue))
                }
                return ''
              }}
              getOptionSelected={getOptionSelected}
              getOptionDisabled={getOptionDisabled}
              renderInput={renderInput}
              multiple={multiple}
              data-testid={`form-autocomplete${multiple ? '-multiple' : ''}`}
              inputValue={inputValue}
              onInputChange={onInputChange}
              onBlur={onBlur}
              onKeyUp={onKeyUp}
              noOptionsText={creatable ? t(`No options, type to add.`) : t(`No options`)}
              blurOnSelect
          />
          {
            desc
              ? <DescText>{desc}</DescText>
              : null
          }
      </FieldWrapper>
    )
    : null;
}

export const FormAutocomplete = ({ stateful = true, ...baseProps }) => {
  return stateful
    ? statefulField(BaseAutoComplete)(baseProps)
    : <BaseAutoComplete {...baseProps} />
};

export const FormAutocompleteMultiple = ({
  ...autocompleteProps
}) => {
  const { mapLabel = 'name' } = autocompleteProps

  return (
    <FormAutocomplete
      {...autocompleteProps}
      renderOption={(option, { selected }) => (
        <React.Fragment>
          <Checkbox
            checked={selected}
          />
          {option[mapLabel]}
        </React.Fragment>
      )}
      multiple
    />
  )
}

export default FormAutocomplete;
